import { useGlobal } from "@/app/providers/use-global";
import { useEffect, useMemo, useState } from "react";

const safariRegex = /^((?!chrome|android).)*safari/i;

const getArrFromStr = (str: string) =>
  str.split(" ").reduce((pV, cV, i) => {
    if (i % 2 === 0) {
      pV.push(cV);
    } else {
      const lastElement = pV.at(-1);
      pV[pV.length - 1] = `${lastElement} ${cV}`;
    }
    return pV;
  }, [] as string[]);

type Props = {
  string: string;
  speed?: number;
  batchSize?: number; // Number of words to add per update
};

const useTypeAnimation = ({ string, speed = 20, batchSize = 1 }: Props) => {
  const { isIframe } = useGlobal();
  const [hasStarted, setHasStarted] = useState(false);
  const [hasCompletedTyping, setHasCompletedTyping] = useState(false);
  const [displayResponse, setDisplayResponse] = useState("");
  const arr = useMemo(() => getArrFromStr(string), [string]);

  // Detect Safari browser
  const isSafari = useMemo(() => {
    if (typeof window !== "undefined" && typeof navigator !== "undefined") {
      return safariRegex.test(navigator.userAgent);
    }
    return false;
  }, []);

  const adjustedBatchSize = isSafari && isIframe ? 5 : isSafari ? 3 : batchSize; // Safari updates more frequently

  const start = () => setHasStarted(true);

  const noAnimation = () => setDisplayResponse(string);

  useEffect(() => {
    if (displayResponse) {
      return;
    }
    if (!hasStarted) {
      return;
    }

    setHasCompletedTyping(false);

    let i = 0;
    let lastUpdateTime = Date.now();

    const updateText = () => {
      const now = Date.now();
      if (now - lastUpdateTime >= speed) {
        setDisplayResponse(arr.slice(0, i).join(" "));
        i += adjustedBatchSize;
        lastUpdateTime = now;
      }

      if (i <= arr.length) {
        requestAnimationFrame(updateText);
      } else {
        setDisplayResponse(string);
        setHasCompletedTyping(true);
      }
    };

    const id = requestAnimationFrame(updateText);

    return () => {
      setHasCompletedTyping(true);
      cancelAnimationFrame(id);
    };
  }, [hasStarted, adjustedBatchSize, speed]);

  return {
    hasCompletedTyping,
    displayResponse,
    start,
    noAnimation,
  };
};

export default useTypeAnimation;
